import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import { ReactElement, ReactNode, useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/design-system/components/ui';

type Severity = 'error' | 'warning' | 'info' | 'success';

interface AlertOptions {
  bodyText: string;
  severity: Severity;
  className?: string;
  titleText?: string;
}

interface UseAlertReturnType {
  AlertComponent: () => ReactElement | null;
  closeAlert: () => void;
  showAlert: (options: AlertOptions) => void;
}

interface Props {
  afterClose?: () => void;
}

const iconMap: Record<Severity, ReactNode> = {
  info: <Info size={20} />,
  success: <CheckCircle size={20} />,
  warning: <AlertTriangle size={20} />,
  error: <AlertCircle size={20} />,
};

const useAlert = ({ afterClose }: Partial<Props> = {}): UseAlertReturnType => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AlertOptions>({
    titleText: '',
    bodyText: '',
    severity: 'success',
    className: '',
  });

  const showAlert = ({ severity, className, titleText, bodyText }: AlertOptions) => {
    setOptions({ severity, className, titleText, bodyText });
    setOpen(true);
  };

  const closeAlert = () => {
    setOpen(false);
    afterClose?.();
  };

  const AlertComponent = (): ReactElement | null => {
    if (!open) return null;

    return (
      <div className="tw:w-full tw:mb-4">
        <Alert variant={options.severity} icon={iconMap[options.severity]} className={options.className}>
          <div className="tw:flex tw:justify-between tw:items-start tw:w-full">
            <div>
              {options.titleText && <AlertTitle>{options.titleText}</AlertTitle>}
              <AlertDescription>{options.bodyText}</AlertDescription>
            </div>
            <button
              onClick={closeAlert}
              aria-label="Close alert"
              className="tw:shrink-0 tw:ml-2 tw:opacity-70 tw:hover:opacity-100 tw:transition-opacity"
            >
              <X size={16} />
            </button>
          </div>
        </Alert>
      </div>
    );
  };

  return {
    showAlert,
    closeAlert,
    AlertComponent,
  };
};

export default useAlert;
